import classNames from "classnames/dedupe";
import React, {
  FC,
  useContext,
  ReactNode,
  useEffect,
  useCallback,
  useRef,
} from "react";

import { accordionContext } from "./AccordionContext";

export interface IAccordionDetailsProps {
  /**
   * Sets a custom html element for this component
   *
   * @default 'div'
   */
  as?: React.ElementType;
  /**
   * The custom set of css classes used to style component
   */
  classes?: {
    /**
     * applied to outer 'div' element
     */
    root?: string;
    toggled?: string;
  };
  children?: ReactNode;
}

function getElementHeight(el: HTMLElement) {
  const { marginTop, marginBottom } = getComputedStyle(el);
  return parseFloat(marginTop) + parseFloat(marginBottom) + el.scrollHeight;
}

function getStringHeight(fontSize: string): number {
  const fz = parseFloat(fontSize);
  if (!Number.isNaN(fz)) return fz;
  return 0;
}

function getContentHeight(el: HTMLElement) {
  const { fontSize } = getComputedStyle(el);
  return Array.from(el.childNodes).reduce((a, v) => {
    if (v instanceof HTMLElement) {
      return a + getElementHeight(v);
    }
    return a + getStringHeight(fontSize);
  }, el.scrollHeight);
}

const AccordionDetails: FC<IAccordionDetailsProps> = ({
  as: Component = "div",
  children,
  classes,
  ...other
}) => {
  const context = useContext(accordionContext);

  const container = useRef<HTMLElement>();

  const rootClasses = classNames("uds-height", classes?.root, {
    "uds-height--active": !context?.expanded,
    [classes?.toggled]:
      typeof classes?.toggled !== "undefined" && context?.expanded,
  });

  const setContainerHeight = useCallback(() => {
    if (container.current) {
      const height = getContentHeight(container.current);
      container.current.style.setProperty(
        "--accordion-max-height",
        `${height}px`,
      );
    }
  }, []);

  const handleMutate = useCallback<MutationCallback>(() => {
    setContainerHeight();
  }, [setContainerHeight]);

  useEffect(() => {
    setContainerHeight();
    const observer = new MutationObserver(handleMutate);
    if (container.current) {
      observer.observe(container.current, {
        childList: true,
        attributeFilter: ["class"],
      });
    }
    return () => {
      observer.disconnect();
    };
  }, [handleMutate, setContainerHeight]);

  return (
    <Component
      ref={container}
      data-testid="accordion-details"
      className={rootClasses}
      {...other}
    >
      {children}
    </Component>
  );
};

export default AccordionDetails;
